import { type ReactElement, useState } from 'react';
import type { ApiError } from '@vello/api-client';
import { Button, Field, Notice, Select, Stack, Text, TextInput } from '@vello/web-theme';
import { mayInviteStoreResponsible, resolveLogin, type LoginResolution } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

type Role = 'owner' | 'manager';
type Msg = { tone: 'success' | 'danger' | 'warning'; text: string; link?: string };
const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;

/**
 * D849 (P0-009) - "Convidar responsável": the one place that puts a person in charge of a store, end to end.
 *
 *  1. the person's store sign-in (a MERCHANT-realm login) is found - or created with the realm-aware operator invitation, and its link shown
 *     once when no e-mail goes out;
 *  2. the person joins the store's team as owner (or manager), which also gives the MERCHANT grant the Loja authorizes against;
 *  3. when the invitation is accepted the person signs in to the Loja and finds the store.
 *
 * The membership is granted by the new login's id, not by its e-mail: the server refuses an e-mail whose login has not accepted its invitation
 * yet, and the whole point is that the team is ready before the person signs in for the first time. A login of the Vello team is never reused
 * (it cannot open the Loja). The server authorizes each call; an operator without the sign-in word is told to ask for a login that exists.
 */
export function StoreResponsibleInvite({ merchantId, countryCode, onDone, primary = false }: { merchantId: string; countryCode: string | undefined; onDone: () => void; primary?: boolean }): ReactElement {
  const { runtime, grants, me, markets } = useAdmin();
  const { t } = useT();
  const { fail, explain: said } = useVocab();
  const mayCreate = mayInviteStoreResponsible(grants);
  const [form, setForm] = useState({ email: '', name: '', phone: '', role: 'owner' as Role });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const set = (k: 'email' | 'name' | 'phone', value: string): void => setForm((f) => ({ ...f, [k]: value }));
  const country = (countryCode ?? me?.countryCode ?? '').toUpperCase();
  const zone = markets.find((m) => m.countryCode.toUpperCase() === country)?.timezone ?? me?.timezone;
  const refuse = (e: ApiError): Msg => ({ tone: 'danger', text: said(e) ?? t('pob.refused', { code: fail(e) }) });
  const refused = (reason: Extract<LoginResolution, { kind: 'refused' }>['reason']): Msg => ({ tone: 'danger', text: t(`pob.owner.refused.${reason}`) });

  const submit = async (): Promise<void> => {
    setMsg(undefined);
    const email = form.email.trim().toLowerCase();
    if (!EMAIL.test(email)) {
      setMsg({ tone: 'danger', text: t('pob.owner.refused.EMAIL') });
      return;
    }
    setBusy(true);
    let userId: string | undefined;
    let link: string | undefined;
    let created = false;
    if (mayCreate) {
      const listed = await runtime.operatorsAdmin.list();
      if (!listed.ok) {
        setBusy(false);
        setMsg(refuse(listed.error));
        return;
      }
      const resolved = resolveLogin({ email, name: form.name, phoneE164: form.phone.replace(/[\s()-]/g, '') }, listed.value.operators, {
        realm: 'MERCHANT', countryCode: country, ...(me?.locale !== undefined ? { locale: me.locale } : {}), ...(zone !== undefined ? { timezone: zone } : {}), reasonCode: 'operator_onboarding',
      });
      if (resolved.kind === 'refused') {
        setBusy(false);
        setMsg(refused(resolved.reason));
        return;
      }
      if (resolved.kind === 'existing') userId = resolved.userId;
      else {
        const made = await runtime.operatorsAdmin.invite(resolved.invite);
        if (!made.ok) {
          setBusy(false);
          setMsg(refuse(made.error));
          return;
        }
        userId = made.value.operator.userId;
        created = true;
        if (made.value.invitation.delivery === 'MANUAL') link = made.value.invitation.url;
      }
    }
    // Without the sign-in word the person must already have a store login: the team is joined by e-mail (the server checks the realm).
    const joined = await runtime.merchantConsole.grantMembership(userId !== undefined ? { userId, merchantId, role: form.role } : { identifier: email, merchantId, role: form.role });
    setBusy(false);
    if (!joined.ok) {
      // The login may exist by now (and its one-time link with it): keep the link, a retry finds the login and only adds the team place.
      setMsg({ ...refuse(joined.error), ...(link !== undefined ? { link } : {}) });
      return;
    }
    setForm({ email: '', name: '', phone: '', role: form.role });
    setMsg(link !== undefined ? { tone: 'warning', text: t('pob.owner.done.manual'), link } : { tone: 'success', text: t(created ? 'pob.owner.done' : 'pob.owner.done.existing', { email }) });
    onDone();
  };

  return (
    <Stack gap={3} data-testid="store-responsible-invite">
      <Text role="caption" tone="secondary" as="p">{t(mayCreate ? 'pob.owner.hint' : 'pob.owner.hint.existing')}</Text>
      <Field label={t('pob.owner.email')} htmlFor="owner-email"><TextInput id="owner-email" type="email" autoComplete="off" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
      {mayCreate && (
        <>
          <Field label={t('pob.owner.name')} htmlFor="owner-name" hint={t('pob.owner.newOnly')}><TextInput id="owner-name" value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
          <Field label={t('pob.owner.phone')} htmlFor="owner-phone" hint={t('pob.owner.newOnly')}><TextInput id="owner-phone" type="tel" className="tnum" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
        </>
      )}
      <Field label={t('pob.member.role')} htmlFor="owner-role">
        <Select id="owner-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}>
          <option value="owner">{t('pob.owner.role.owner')}</option>
          <option value="manager">{t('pob.owner.role.manager')}</option>
        </Select>
      </Field>
      {msg !== undefined && (
        <Notice tone={msg.tone}>
          {msg.text}
          {msg.link !== undefined && <Text as="code" mono block data-testid="owner-manual-link">{msg.link}</Text>}
        </Notice>
      )}
      <div>
        <Button variant={primary ? 'primary' : 'secondary'} disabled={busy || form.email.trim() === ''} onClick={() => void submit()} data-testid="owner-invite-send">{t('pob.owner.invite')}</Button>
      </div>
    </Stack>
  );
}
